/**
 * productHighlights(product)
 * Returns up to 4 scannable highlights derived from real product fields.
 * Highlights are shown ONLY when they are demonstrably true for that product —
 * we scan description / material / caption / title / category / size fields.
 * The result is deduped and topped up with safe brand-truth defaults so every
 * PDP always shows exactly 4 highlights in a compact luxury row.
 */
export function productHighlights(product) {
  if (!product) return [];
  const desc = String(product.description || "").toLowerCase();
  const material = String(product.material || "").toLowerCase();
  const caption = String(product.caption || "").toLowerCase();
  const title = String(product.title || "").toLowerCase();
  const cat = String(product.category || "").toLowerCase();
  const care = String(product.care || "").toLowerCase();
  const text = `${desc} ${material} ${caption} ${title} ${care}`;

  const out = [];
  const push = (s) => {
    if (s && !out.includes(s)) out.push(s);
  };

  // 1) Material / weave — pick the strongest hit
  if (/sateen/.test(text)) push("Sateen cotton weave");
  else if (/percale/.test(text)) push("Cool percale weave");
  else if (/linen/.test(text) || /linen/.test(cat)) push("Pure premium linen");
  else if (/silk/.test(text)) push("Pure silk finish");
  else if (/velvet/.test(text)) push("Plush velvet");
  else if (/cashmere/.test(text)) push("Cashmere blend");
  else if (/cotton/.test(text)) push("Premium cotton");
  else if (/wool/.test(text)) push("Fine wool");
  else if (/microfib(er|re)/.test(text)) push("Soft microfibre");

  // 2) Thread count if declared
  const tc = text.match(/(\d{3,4})\s*tc\b/);
  if (tc) push(`${tc[1]} TC luxury weave`);

  // 3) Design / craft detail
  if (/scallop/.test(text)) push("Scalloped trim");
  else if (/hand[- ]?embroider|chain[- ]?stitch/.test(text)) push("Hand embroidered");
  else if (/embroider/.test(text)) push("Embroidered detail");
  else if (/hand[- ]?block|block[- ]?print/.test(text)) push("Hand block printed");
  else if (/quilted|quilting/.test(text)) push("Hand quilted");
  else if (/pleated|pleat/.test(text)) push("Pleated finish");
  else if (/monogram|initials|bespoke|personalis/.test(text) || /monogram/.test(cat)) push("Bespoke monogram");
  else if (/piped|piping/.test(text)) push("Piped edging");
  else if (/border/.test(text)) push("Signature border");

  // 4) Set contents
  if (/shams?/.test(text) || /pillow ?case|pillow cover/.test(text)) push("Matching shams included");
  else if (/set includes|includes .+ (sheet|cover|case|sham)/.test(text)) push("Complete set");
  else if (/duvet/.test(cat)) push("Matching shams available");

  // 5) Custom sizes
  const inches = Array.isArray(product.size_inches) ? product.size_inches.length : 0;
  const sizes = Array.isArray(product.sizes) ? product.sizes.length : 0;
  if (inches > 0) push("Custom sizes");
  else if (sizes >= 3) push("Multiple sizes");

  // Category-driven bonus signals
  if (/hotel|five[- ]?star/.test(text) || /hotel/.test(cat)) push("Hotel-grade quality");
  if (/curtain/.test(cat) && /black[- ]?out/.test(text)) push("Blackout lining");

  // Truthful defaults to always end up with 4
  const defaults = ["Crafted in India", "Made to order", "International shipping", "Premium finish"];
  for (const d of defaults) {
    if (out.length >= 4) break;
    push(d);
  }
  return out.slice(0, 4);
}
